import { Avatar, Badge, Icon, Popover, Table } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import React, { FC, useContext } from 'react';
import styled from 'styled-components';
import { AppContext } from '../../core/contexts/AppContext';
import { BundlePool, BundleProfile } from '../index';
import { history } from '../../core/components/App/history';

const Styles = styled.div`
  .pool {
    margin-bottom: 24px;
  }

  .poolHeading {
    display: flex;
    align-items: baseline;
    margin-bottom: 8px;
  }

  .poolTitle {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
  }

  .poolEmpty {
    font-size: 13px;
    color: #a1a1aa;
  }

  .table-row {
    cursor: pointer;
  }

  .names {
    margin-left: 24px;
  }

  .displayName {
    font-size: 16px;
    display: block;
    color: #333;
    font-weight: 800;
  }

  .locationName {
    color: #a1a1aa;
  }

  .rowItem {
    display: flex;
    flex-direction: row;
  }

  .visitTypeWarn {
    font-weight: 600;
    color: #d97706;
    background-color: #fef3c7;
    padding: 4px 8px;
    border-radius: 6px;
  }

  .visitTypeInfo {
    font-weight: 600;
    color: #1f2937;
    background-color: #f3f4f6;
    padding: 4px 8px;
    border-radius: 6px;
  }
`;

const columns = (): ColumnProps<BundleProfile>[] => [
  {
    title: '',
    width: 10,
    key: 'active',
    render: (record: BundleProfile) => <Badge status={record.isActive ? 'success' : 'default'} />,
  },
  {
    title: 'Name',
    key: 'displayName',
    render: (record: BundleProfile) => (
      <div className="rowItem">
        {record.profileImgUrl ? (
          <Avatar size="large" src={record.profileImgUrl} shape="square" />
        ) : (
          <Avatar size="large" icon="user" shape="square" />
        )}
        <div className="names">
          <span className="displayName">{record.displayName}</span>
          <span className="locationName">{record.location?.name}</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Location',
    key: 'location',
    render: (record: BundleProfile) => (
      <div>
        <div>{record.location?.address1}</div>
        <div>
          {record.location?.city}, {record.location?.state} {record.location?.postalCode}
        </div>
      </div>
    ),
  },
  {
    title: 'Visit Types',
    key: 'visitTypes',
    render: (record: BundleProfile) => {
      const procedures = record.profileProcedures || [];
      const content = procedures.map(pp => (
        <p key={pp.id}>
          {pp?.procedure?.specialty?.name} &gt; {pp?.procedure?.name}
        </p>
      ));

      if (procedures.length === 0) {
        return (
          <span className="visitTypeWarn">
            <Icon type="warning" theme="filled" />
            &nbsp;No Visit Types Configured
          </span>
        );
      }

      return (
        <Popover content={content}>
          <span className="visitTypeInfo">
            <Icon type="info" theme="filled" />
            &nbsp;{procedures.length} Visit Type Configured
          </span>
        </Popover>
      );
    },
  },
];

interface BundlePoolsProps {
  pools: BundlePool[];
}

/**
 * The profiles a bundled profile is bundled with, grouped by pool.
 *
 * A booking takes one slot from each pool in order, so membership is shown per pool rather than as
 * one flat list. Pools come from the bundled profile's schedule and are read-only here.
 */
export const BundlePools: FC<BundlePoolsProps> = ({ pools }: BundlePoolsProps): JSX.Element => {
  const { currentOrganization } = useContext(AppContext);

  if (pools.length === 0) {
    return (
      <Styles>
        <p className="poolEmpty">
          No bundled profiles yet. Add schedules to this profile&apos;s bundle pools to populate this list.
        </p>
      </Styles>
    );
  }

  return (
    <Styles>
      {pools.map((pool, index) => (
        <div className="pool" key={pool.order} id={`bundle-pool-${index + 1}`}>
          <div className="poolHeading">
            <span className="poolTitle">Pool {index + 1}</span>
          </div>

          {pool.profiles.length === 0 ? (
            <p className="poolEmpty">No profiles are attached to the schedules in this pool.</p>
          ) : (
            <Table<BundleProfile>
              rowClassName={() => 'table-row'}
              showHeader={true}
              dataSource={pool.profiles}
              columns={columns()}
              rowKey={record => record.id}
              pagination={false}
              onRow={record => ({
                onClick: event => {
                  event.preventDefault();
                  history.push(`/organizations/${currentOrganization?.id}/profiles/${record.id}/edit`);
                },
              })}
            />
          )}
        </div>
      ))}
    </Styles>
  );
};
